import Image from "next/image"
import Link from "next/link"
import { ArrowLongRightIcon } from "@heroicons/react/24/outline"
import TextLink from "../../common/textLink"
import { Insights } from "~/content/navLinks"
import { type FilterType } from "./blogArticleSection"
import { absoluteUrl, formatDate } from "~/app/utils/helper"
import type { DrupalNode, JsonApiResource } from "next-drupal"
import parse from "html-react-parser"
import NoSsr from "../../common/noSsr"

export type ArticleGridVariant =
  | "image-top" // classic stacked w image-on-top
  | "image-bg" // image as bg with text overlaid on bottom left

export type BlogProps = {
  blog: DrupalNode | JsonApiResource
  feature?: boolean // rowspan2
  variant?: ArticleGridVariant
  resetFilters: (type: keyof FilterType, value: string) => void
  tagColor?: "light" | "dark"
}

const Blog = (props: BlogProps) => {
  const blog = props.blog
  const author = (blog.field_displayed_author?.name as string) || "Incluso Team"
  const blogPath = Insights.href + "/blog/" + blog.path.alias
  const blogTags = [
    ...blog.field_tags.map((tag: { name: string }) => ({
      tag: tag.name,
      type: "tag",
    })),
  ]
  // .replace(/\n+/g, "").trim() --> remove line break to show on blog grid -- PENDING
  const body = blog.body?.processed ? (parse(blog.body.processed.replace(/\n+/g, "").trim()) as string) : undefined

  // handle tag click
  const handleTagClick = (type: keyof FilterType, tag: string) => {
    props.resetFilters(type, tag)
  }

  const tagColor = props.tagColor ?? "dark"

  if (props.variant === "image-bg") {
    return (
      <>
        <article
          className={`relative ${props.feature ? "row-span-2 min-h-[360px] md:min-h-[560px]" : "min-h-[180px] md:min-h-[280px]"} flex flex-col overflow-hidden rounded-3xl`}
        >
          <Link href={blogPath} className="peer absolute inset-0 z-[5]" />
          <div className="absolute inset-0 z-[1] bg-gradient-blue1/70 transition duration-300 peer-hover:bg-gradient-blue1/30" />
          {blog.field_image && (
            <Image
              fill
              src={absoluteUrl(blog.field_image.uri.url as string)}
              alt={(blog.field_image.resourceIdObjMeta.alt as string) ?? "blog-image"}
              className="scale-105 object-cover object-top opacity-70 transition duration-300 peer-hover:scale-[1.15]"
            />
          )}

          <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-2 p-4 text-xs font-bold">
            {/* update slice to (0,3) after testing */}
            {blogTags.slice(0, 6).map((tag, i) => (
              <button
                key={i}
                className={`relative z-10 rounded-full px-5 py-1.5 transition ${tagColor === "light" ? "bg-white/10 text-green hover:bg-white/20" : tagColor === "dark" ? "bg-black/55 text-white hover:text-green-light" : ""}`}
                onClick={() => handleTagClick(tag.type as keyof FilterType, tag.tag)}
              >
                {tag.tag}
              </button>
            ))}
          </div>

          <div className="z-[3] mt-auto px-4 pb-2 pt-4 text-white transition duration-300 peer-hover:text-green-light">
            <div className="flex items-center gap-x-4 pt-2 text-xs">
              {author && (
                <div className="group relative flex items-center gap-x-4">
                  <p className="text-gray-200">{author}</p>
                </div>
              )}
              <div className="text-gray-200">{formatDate(blog.created)}</div>
            </div>
            <div className="relative">
              <h3 className="text-resp-xl font-bold leading-6 md:mt-2">{blog.title}</h3>
              <div className="prose-p:text-resp-sm prose-headings:text-resp-sm prose prose-invert mt-1 line-clamp-2 whitespace-pre-line text-gray-200 hover:text-green-light prose-headings:font-normal prose-strong:font-normal">
                {body}
              </div>
            </div>
          </div>

          <div className="text-resp-sm z-20 ml-auto w-fit px-4 pb-4 font-bold peer-hover:text-green-light">
            <TextLink href={blogPath} className="flex items-center text-white" hoverColor="hover:text-green-light">
              Keep reading
              <ArrowLongRightIcon className="ml-1 h-4 w-4" />
            </TextLink>
          </div>
        </article>
      </>
    )
  } else {
    return (
      <article className="flex flex-col items-start">
        <div className="group relative mx-auto w-4/5 sm:w-2/3 lg:w-full">
          <Link href={blogPath}>
            <div className="aspect-[2/1] w-full lg:aspect-[3/2]" />
            {blog.field_image && (
              <Image
                fill
                src={absoluteUrl(blog.field_image.uri.url as string)}
                alt={(blog.field_image.resourceIdObjMeta.alt as string) ?? "blog-image"}
                className="aspect-[16/9] w-full rounded-2xl bg-white/20 object-cover transition group-hover:opacity-90 sm:aspect-[2/1] lg:aspect-[3/2]"
              />
            )}
            <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/20" />
          </Link>
        </div>
        <div>
          <div className="-ml-1 flex flex-wrap items-center gap-x-4 gap-y-2 pt-6 text-xs">
            {/* update slice to (0,3) after testing */}
            {blogTags.slice(0, 3).map((tag, i) => (
              <Link
                key={i}
                href={`${Insights.href}?${tag.type}=${tag.tag}`}
                className={`relative z-10 rounded-full px-5 py-1.5 transition ${tagColor === "light" ? "bg-white/10 text-green hover:bg-white/20" : tagColor === "dark" ? "bg-black/55 text-white hover:text-green-light" : ""}`}
              >
                {tag.tag}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-x-4 pt-4 text-xs">
            <div className="text-gray-500">
              <NoSsr>{formatDate(blog.created)}</NoSsr>
            </div>
          </div>
          <div className="group relative">
            <h3 className="text-resp-lg group-hover:text-ct-blue-light mt-2 font-semibold leading-6 text-white transition">
              <a href={blogPath}>
                <span className="absolute inset-0" />
                {blog.title}
              </a>
            </h3>
            <div className="text-resp-sm mt-5 line-clamp-3 leading-6 text-gray-300">{body}</div>
          </div>
          {author && (
            <div className="group relative mt-4 flex items-center gap-x-4">
              <p className="text-resp-sm font-semibold leading-6 text-white">{author}</p>
            </div>
          )}
        </div>
      </article>
    )
  }
}

export default Blog
